import { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import GameService from '../services/GameService';
import LanguageStore from '../store/LanguageStore';
import { observer } from 'mobx-react-lite';
import CountryGuessGame from '../components/CountryGuessGame/CountryGuessGame';
import './game-page.scss';

const GamePage = observer(() => {
    const { sessionId } = useParams();
    const navigate = useNavigate();
    const t = LanguageStore.t;
    const [session, setSession] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [gameCountry, setGameCountry] = useState(null);
    const [guessedCountries, setGuessedCountries] = useState(new Set());
    const [playerId, setPlayerId] = useState(null);
    const [opponentProgress, setOpponentProgress] = useState([]);
    const [myScore, setMyScore] = useState(0);
    const [opponentScore, setOpponentScore] = useState(0);
    const [myErrors, setMyErrors] = useState(0);
    const [opponentErrors, setOpponentErrors] = useState(0);
    const [timeLeft, setTimeLeft] = useState(300);
    const [isPlayer1, setIsPlayer1] = useState(false);

    useEffect(() => {
        const joinSession = async () => {
            try {
                const playerId = GameService.getPlayerId();
                setPlayerId(playerId);
                
                const result = await GameService.joinSession(sessionId);
                
                if (result.success) {
                    setSession(result.session);
                    const isP1 = result.session.player1Id === playerId;
                    setIsPlayer1(isP1);
                    
                    // Устанавливаем начальные значения счета
                    if (isP1) {
                        setMyScore(result.session.player1Score || 0);
                        setOpponentScore(result.session.player2Score || 0);
                        setMyErrors(result.session.player1Errors || 0);
                        setOpponentErrors(result.session.player2Errors || 0);
                        setOpponentProgress(result.session.player2Progress || []);
                        // Устанавливаем угаданные страны для первого игрока
                        if (result.session.player1Progress && Array.isArray(result.session.player1Progress)) {
                            setGuessedCountries(new Set(result.session.player1Progress));
                        }
                    } else {
                        setMyScore(result.session.player2Score || 0);
                        setOpponentScore(result.session.player1Score || 0);
                        setMyErrors(result.session.player2Errors || 0);
                        setOpponentErrors(result.session.player1Errors || 0);
                        setOpponentProgress(result.session.player1Progress || []);
                        // Устанавливаем угаданные страны для второго игрока
                        if (result.session.player2Progress && Array.isArray(result.session.player2Progress)) {
                            setGuessedCountries(new Set(result.session.player2Progress));
                        }
                    }
                    
                    const firstCountry = result.session.countries[0];
                    if (firstCountry) {
                        setGameCountry(firstCountry);
                    }
                    
                    // startProgressSync будет вызван в отдельном useEffect
                } else {
                    setError(result.error || 'Failed to join session');
                }
            } catch (err) {
                setError(err.message || 'Connection error');
            } finally {
                setLoading(false);
            }
        };

        if (sessionId) {
            joinSession();
        }
    }, [sessionId]);

    // Синхронизация прогресса через Mercure WebSocket
    useEffect(() => {
        if (!session || !playerId) return;

        const sessionId = session.sessionId;
        const isP1 = session.player1Id === playerId;

        const startProgressSync = async () => {
            const mercureUrl = process.env.REACT_APP_MERCURE_URL || 'http://localhost:3001/.well-known/mercure';
            const url = new URL(mercureUrl);
            url.searchParams.append('topic', `game-session/${sessionId}`);
            
            // Получаем JWT токен для Mercure
            // Mercure принимает токен через query параметр authorization без префикса "Bearer"
            try {
                const token = await GameService.getMercureToken();
                url.searchParams.append('authorization', token);
            } catch (error) {
                console.error('Failed to get Mercure token:', error);
                // Продолжаем без токена, возможно Mercure разрешит анонимный доступ
            }

            const eventSource = new EventSource(url);
            let pollingInterval = null;

            eventSource.onmessage = (event) => {
                try {
                    const data = JSON.parse(event.data);
                    if (data.type === 'progress_updated') {
                        // Определяем какой игрок мы
                        // Обновляем счет, но НЕ обновляем guessedCountries если это обновление от соперника
                        // guessedCountries обновляется только при наших собственных ответах
                        if (isP1) {
                            setMyScore(data.player1Score || 0);
                            setOpponentScore(data.player2Score || 0);
                            setMyErrors(data.player1Errors || 0);
                            setOpponentErrors(data.player2Errors || 0);
                            setOpponentProgress(data.player2Progress || []);
                            // НЕ обновляем guessedCountries при обновлениях от соперника
                        } else {
                            setMyScore(data.player2Score || 0);
                            setOpponentScore(data.player1Score || 0);
                            setMyErrors(data.player2Errors || 0);
                            setOpponentErrors(data.player1Errors || 0);
                            setOpponentProgress(data.player1Progress || []);
                            // НЕ обновляем guessedCountries при обновлениях от соперника
                        }
                        // НЕ обновляем gameCountry здесь - это делается только при правильном ответе
                    }
                } catch (err) {
                    console.error('Error parsing Mercure message:', err);
                }
            };

            eventSource.onerror = (error) => {
                console.error('Mercure connection error:', error);
                // Не закрываем соединение сразу - EventSource может переподключиться автоматически
                // Если readyState === 2 (CLOSED), значит соединение закрыто окончательно
                if (eventSource.readyState === EventSource.CLOSED) {
                    // Fallback на polling при окончательном закрытии
                    if (!pollingInterval) {
                        pollingInterval = setInterval(async () => {
                            try {
                                const result = await GameService.getSession(sessionId);
                                if (result.success) {
                                    const session = result.session;
                                    
                                    if (isP1) {
                                        setMyScore(session.player1Score || 0);
                                        setOpponentScore(session.player2Score || 0);
                                        setOpponentProgress(session.player2Progress || []);
                                    } else {
                                        setMyScore(session.player2Score || 0);
                                        setOpponentScore(session.player1Score || 0);
                                        setOpponentProgress(session.player1Progress || []);
                                    }
                                }
                            } catch (err) {
                                console.error('Error syncing progress:', err);
                            }
                        }, 2000);
                    }
                }
            };

            eventSource.onopen = () => {
                // Если был polling, останавливаем его
                if (pollingInterval) {
                    clearInterval(pollingInterval);
                    pollingInterval = null;
                }
            };

            // Возвращаем cleanup функцию
            return () => {
                if (pollingInterval) {
                    clearInterval(pollingInterval);
                }
                if (eventSource.readyState !== EventSource.CLOSED) {
                    eventSource.close();
                }
            };
        };

        let cleanup = null;
        startProgressSync().then(cleanupFn => {
            cleanup = cleanupFn;
        });

        return () => {
            if (cleanup) {
                cleanup();
            }
        };
    }, [session, playerId]);

    // Стабилизируем videoCountries с помощью useMemo, чтобы предотвратить пересоздание вариантов ответов
    // Используем JSON.stringify для стабильной зависимости, так как session.countries - это массив
    const countriesKey = session?.countries ? JSON.stringify(session.countries) : '';
    const videoCountries = useMemo(() => {
        if (!session || !session.countries) return [];
        // Создаем стабильный массив объектов с code
        return session.countries.map(code => ({ code }));
    }, [countriesKey]); // Используем JSON.stringify для стабильной зависимости

    // Получаем следующую неугаданную страну
    const getNextUnguessedCountry = (currentGuessed) => {
        if (!session) return null;
        const unguessed = session.countries.filter(code => !currentGuessed.has(code));
        return unguessed.length > 0 ? unguessed[0] : null;
    };

    // Обработчик неправильного ответа
    // Обработчик продолжения после ошибки (без увеличения счета)
    const handleContinue = () => {
        if (!gameCountry || !session) return;
        
        // Просто переходим к следующей неугаданной стране без обновления прогресса
        const nextCountry = getNextUnguessedCountry(guessedCountries);
        if (nextCountry && nextCountry !== gameCountry) {
            setGameCountry(nextCountry);
        } else if (!nextCountry) {
            // Игра окончена
            setGameCountry(null);
        }
    };

    const handleIncorrectAnswer = async () => {
        const newErrors = myErrors + 1;
        setMyErrors(newErrors);
        
        // Обновляем ошибки на сервере
        if (session && playerId) {
            try {
                await GameService.updateProgress(session.sessionId, playerId, Array.from(guessedCountries), newErrors);
            } catch (err) {
                console.error('Error updating errors:', err);
            }
        }
    };

    // Обработчик правильного ответа (только для правильных ответов!)
    const handleCorrectAnswer = async () => {
        if (!gameCountry || !session || !playerId) return;
        
        // Проверяем, была ли страна уже угадана
        // Если страна УЖЕ в guessedCountries, значит это повторный вызов - игнорируем
        if (guessedCountries.has(gameCountry)) {
            return;
        }
        
        // Страна НЕ в guessedCountries - это правильный ответ
        // Добавляем страну в угаданные и обновляем прогресс
        const newGuessed = new Set([...guessedCountries, gameCountry]);
        setGuessedCountries(newGuessed);
        
        // Обновляем прогресс на сервере
        try {
            await GameService.updateProgress(session.sessionId, playerId, Array.from(newGuessed), myErrors);
        } catch (err) {
            console.error('Error updating progress:', err);
            // Продолжаем даже если обновление не удалось
        }
        
        // Переходим к следующей стране
        const nextCountry = getNextUnguessedCountry(newGuessed);
        if (nextCountry && nextCountry !== gameCountry) {
            setGameCountry(nextCountry);
        } else if (!nextCountry) {
            // Игра окончена
            setGameCountry(null);
        }
    };

    if (loading) {
        return (
            <div className="game-page">
                <div className="game-page__loading">
                    <div className="game-page__spinner"></div>
                    <p>{t('connecting')}</p>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="game-page">
                <div className="game-page__error">
                    <p>{error}</p>
                    <button onClick={() => navigate('/')}>{t('close')}</button>
                </div>
            </div>
        );
    }

    return (
        <div className="game-page">
            <div className="game-page__header">
                <div className="game-page__scores">
                    <div className="game-page__score">
                        <span className="game-page__score-label">{t('yourScore')}</span>
                        <span className="game-page__score-value">
                            {myScore}
                            {myErrors > 0 && (
                                <span className="game-page__score-errors">/{myErrors}</span>
                            )}
                        </span>
                    </div>
                    <div className="game-page__timer">
                        <span className="game-page__timer-label">{t('timeLeft')}</span>
                        <span className="game-page__timer-value">{Math.floor(timeLeft / 60)}:{(timeLeft % 60).toString().padStart(2, '0')}</span>
                    </div>
                    <div className="game-page__score">
                        <span className="game-page__score-label">{t('opponentScore')}</span>
                        <span className="game-page__score-value">
                            {opponentScore}
                            {opponentErrors > 0 && (
                                <span className="game-page__score-errors">/{opponentErrors}</span>
                            )}
                        </span>
                    </div>
                </div>
            </div>

            {gameCountry && session && (
                <CountryGuessGame
                    key={`${gameCountry}-${session.sessionId}`}
                    countryCode={gameCountry}
                    videoCountries={videoCountries}
                    onClose={() => navigate('/')}
                    onCorrectAnswer={handleCorrectAnswer}
                    onIncorrectAnswer={handleIncorrectAnswer}
                    onContinue={handleContinue}
                    myScore={myScore}
                    opponentScore={opponentScore}
                    myErrors={myErrors}
                    opponentErrors={opponentErrors}
                />
            )}
        </div>
    );
});

export default GamePage;

